import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { useAuth } from '../hooks/useAuth';

export default function AccessCodePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const urlCode = searchParams.get('code') || '';
  const artistName = searchParams.get('artist') || '';

  const [code, setCode] = useState(urlCode);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  // Auto-redeem if logged in and code is in URL
  useEffect(() => {
    if (user && urlCode) {
      setLoading(true);
      apiFetch('/auth/redeem-code', {
        method: 'POST',
        body: JSON.stringify({ code: urlCode.trim() })
      }).then(result => {
        setSuccess(result.artist);
        const dest = (user.role === 'admin') ? '/admin' : '/viewer';
        setTimeout(() => navigate(dest, { replace: true }), 1500);
      }).catch(e => {
        setError(e.message || 'Invalid access code');
        setLoading(false);
      });
    }
  }, [user, urlCode, navigate]);

  const handleSubmit = async () => {
    if (!code.trim()) return;
    setLoading(true);
    setError('');
    try {
      const result = await apiFetch('/auth/redeem-code', {
        method: 'POST',
        body: JSON.stringify({ code: code.trim() })
      });
      setSuccess(result.artist);
      setTimeout(() => {
        navigate('/viewer', { replace: true });
      }, 1500);
    } catch (e) {
      setError(e.message || 'Invalid access code');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    navigate(user ? ((user.role === 'admin') ? '/admin' : '/viewer') : '/login');
  };

  // Success state
  if (success) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32 }}>
        <div style={{ textAlign: 'center', maxWidth: 400 }}>
          <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'rgba(34,197,94,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
            <i className="fa-solid fa-check" style={{ fontSize: 36, color: '#22c55e' }}></i>
          </div>
          <h1 style={{ fontSize: 28, fontWeight: 900, color: 'var(--text)', marginBottom: 8 }}>You're in!</h1>
          <p style={{ fontSize: 16, color: 'var(--text2)', lineHeight: 1.5 }}>
            Connected to <strong style={{ color: 'var(--text)' }}>{success.name}</strong>
          </p>
          <p style={{ fontSize: 13, color: 'var(--text3)', marginTop: 16 }}>Redirecting...</p>
        </div>
      </div>
    );
  }

  // Not logged in — prompt to sign in first, then come back with the code
  if (!user) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32 }}>
        <div style={{ maxWidth: 400, width: '100%', textAlign: 'center' }}>
          <div style={{ width: 64, height: 64, borderRadius: 20, background: 'linear-gradient(135deg,#1E40AF,#6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, color: '#fff', margin: '0 auto 20px' }}>
            <i className="fa-solid fa-key"></i>
          </div>

          <h1 style={{ fontSize: 24, fontWeight: 900, color: 'var(--text)', marginBottom: 6 }}>
            {artistName ? `Join ${artistName}` : 'Join a Team'}
          </h1>
          <p style={{ fontSize: 14, color: 'var(--text3)', lineHeight: 1.5, marginBottom: 24 }}>
            Sign in or create an account to use this access code.
          </p>

          {urlCode && (
            <div style={{ padding: '16px 20px', borderRadius: 14, background: 'var(--card)', border: '2px solid var(--border)', marginBottom: 24 }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', letterSpacing: 1.5, margin: '0 0 6px', textTransform: 'uppercase' }}>Your Code</p>
              <p style={{ fontSize: 24, fontWeight: 900, color: 'var(--blue)', letterSpacing: 3, fontFamily: 'monospace', margin: 0 }}>{urlCode}</p>
            </div>
          )}

          <button onClick={() => navigate(`/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`)}
            style={{ width: '100%', padding: 16, borderRadius: 14, background: 'linear-gradient(135deg,#1E40AF,#1E40AF)', border: 'none', color: '#fff', fontFamily: 'inherit', fontSize: 16, fontWeight: 800, cursor: 'pointer', boxShadow: '0 4px 16px rgba(30,64,175,0.3)', marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <i className="fa-solid fa-arrow-right" style={{ fontSize: 14 }}></i> Sign In to Join
          </button>

          <p style={{ fontSize: 12, color: 'var(--text3)', marginTop: 16 }}>
            Don't have an account?{' '}
            <button onClick={() => navigate(`/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`)}
              style={{ background: 'none', border: 'none', color: 'var(--blue)', fontWeight: 700, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>
              Sign up
            </button>
          </p>
        </div>
      </div>
    );
  }

  // Logged in but no code in URL — show manual entry
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32 }}>
      <div style={{ maxWidth: 400, width: '100%', textAlign: 'center' }}>
        <div style={{ width: 64, height: 64, borderRadius: 20, background: 'linear-gradient(135deg,#1E40AF,#6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, color: '#fff', margin: '0 auto 20px' }}>
          <i className="fa-solid fa-key"></i>
        </div>

        <h1 style={{ fontSize: 24, fontWeight: 900, color: 'var(--text)', marginBottom: 6 }}>Enter Access Code</h1>
        <p style={{ fontSize: 14, color: 'var(--text3)', lineHeight: 1.5, marginBottom: 32 }}>
          Enter your access code to join a team.<br />
          Your tour manager or admin will provide this.
        </p>

        <input
          value={code}
          onChange={e => setCode(e.target.value.toUpperCase())}
          placeholder="ENTER CODE"
          maxLength={20}
          style={{
            width: '100%', padding: '16px 20px', borderRadius: 14, background: 'var(--card)', border: '2px solid var(--border)',
            color: 'var(--text)', fontFamily: 'inherit', fontSize: 18, fontWeight: 700, textAlign: 'center',
            letterSpacing: 3, outline: 'none', boxSizing: 'border-box', textTransform: 'uppercase',
            marginBottom: 16
          }}
          onFocus={e => e.target.style.borderColor = '#1E40AF'}
          onBlur={e => e.target.style.borderColor = 'var(--border)'}
          onKeyDown={e => e.key === 'Enter' && handleSubmit()}
          autoFocus
        />

        {error && (
          <div style={{ padding: '10px 14px', borderRadius: 10, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444', fontSize: 13, marginBottom: 16 }}>
            {error}
          </div>
        )}

        <button
          onClick={handleSubmit}
          disabled={loading || !code.trim()}
          style={{
            width: '100%', padding: 16, borderRadius: 14,
            background: code.trim() ? 'linear-gradient(135deg,#1E40AF,#1E40AF)' : 'var(--card)',
            border: code.trim() ? 'none' : '1px solid var(--border)',
            color: code.trim() ? '#fff' : 'var(--text3)',
            fontFamily: 'inherit', fontSize: 16, fontWeight: 800, cursor: code.trim() ? 'pointer' : 'default',
            boxShadow: code.trim() ? '0 4px 16px rgba(30,64,175,0.3)' : 'none',
            marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8
          }}
        >
          {loading ? (
            <><i className="fa-solid fa-spinner fa-spin" style={{ fontSize: 14 }}></i> Joining...</>
          ) : (
            <><i className="fa-solid fa-arrow-right" style={{ fontSize: 14 }}></i> Join Team</>
          )}
        </button>

        <button
          onClick={handleSkip}
          style={{ background: 'none', border: 'none', color: 'var(--text3)', fontFamily: 'inherit', fontSize: 14, fontWeight: 600, cursor: 'pointer', padding: '8px 16px' }}
        >
          Skip for now
        </button>
      </div>
    </div>
  );
}
